import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import AssignProject from '../interfaces/ip-assign-project';
import AssignedProject from '../interfaces/ip-assigned-project';
import AvailableProject from '../interfaces/ip-available-project';
import EnterpriseProfile from '../interfaces/ip-enterprise-profile';
import NewSupplierUser from '../interfaces/ip-new-supplier-user';
import { PageResponse } from '../interfaces/ip-page-response';
import Pageable from '../interfaces/ip-pageable';
import IpSelectInputOption from '../interfaces/ip-select-input-option';
import SupplierResponse from '../interfaces/ip-supplier-response';
import TaxDTO from '../interfaces/ip-tax-dto';
import UserResponse from '../interfaces/ip-user-response';
import { Account, AccountRequest, IpSupplierManager } from '../interfaces/lip-supplier-manager';
import { IpAuthService } from './ip-auth.service';


@Injectable({
  providedIn: 'root'
})
export class IpSupplierService {
  api = environment.api;

  private allBrokersSubject = new BehaviorSubject<UserResponse[] | null>(null);
  public allBrokers$ = this.allBrokersSubject.asObservable();

  constructor(
    private http: HttpClient,
    private readonly ipAuthService: IpAuthService) {
    this.ipAuthService.currentSession$
      .subscribe(session => {
        if (session) {
          this.getAllBrokers().subscribe(brokers => {
            this.allBrokersSubject.next(brokers);
          });
        }
      });
  }

  // SUPPLIER MANAGERS / BUSINESSES

  getSupplierManagers(
    pageable: Pageable = {},
    searchName: string = '',
    searchValue: string = ''
  ): Observable<PageResponse<SupplierResponse>> {
    let params = new HttpParams();
    if (typeof pageable.page !== 'undefined') {
      params = params.set('page', pageable.page);
    }
    if (pageable.size) {
      params = params.set('size', pageable.size);
    }
    if (pageable.sort) {
      params = params.set('sort', pageable.sort);
    }
    if (searchName && searchValue) {
      params = params.set(searchName, searchValue);
    }
    return this.http.get<PageResponse<SupplierResponse>>(
      `${this.api}/invopay/suppliers`,
      { params: params }
    );
  }

  getSupplierBrokerManagers(
    pageable: Pageable = {},
    searchName: string = '',
    searchValue: string = '',
    isActive?: boolean
  ): Observable<PageResponse<SupplierResponse>> {
    let params = new HttpParams();
    if (typeof pageable.page !== 'undefined') {
      params = params.set('page', pageable.page);
    }
    if (pageable.size) {
      params = params.set('size', pageable.size);
    }
    if (pageable.sort) {
      params = params.set('sort', pageable.sort);
    }
    if (searchName && searchValue) {
      params = params.set(searchName, searchValue);
    }
    if (isActive !== undefined) {
      params = params.set('isActive', isActive)
    }
    return this.http.get<PageResponse<SupplierResponse>>(
      `${this.api}/invopay/suppliers/broker`,
      { params: params }
    );
  }

  getBrokers(): Observable<UserResponse[]> {
    return this.http.get<UserResponse[]>(
      `${this.api}/invopay/enterprises/brokers`
    );
  }

  getAllSupplierManagers(
    pageable: Pageable = {},
    searchName: string = '',
    searchValue: string = '',
    isActive?: boolean
  ): Observable<PageResponse<SupplierResponse>> {
    let params = new HttpParams();
    if (typeof pageable.page !== 'undefined') {
      params = params.set('page', pageable.page);
    }
    if (pageable.size) {
      params = params.set('size', pageable.size);
    }
    if (pageable.sort) {
      params = params.set('sort', pageable.sort);
    }
    if (searchName && searchValue) {
      params = params.set(searchName, searchValue);
    }
    if (isActive !== undefined) {
      params = params.set('isActive', isActive)
    }
    return this.http.get<PageResponse<SupplierResponse>>(
      `${this.api}/invopay/suppliers/all-suppliers`,
      { params: params }
    );
  }

  getSupplierManagerById(id: number): Observable<IpSupplierManager> {
    return this.http.get<IpSupplierManager>(
      `${this.api}/invopay/suppliers/${id}`
    );
  }

  createSupplierManager(
    request: IpSupplierManager
  ): Observable<IpSupplierManager> {
    const formData = this.toFormData(request);
    return this.http.post<IpSupplierManager>(
      `${this.api}/invopay/suppliers`,
      formData
    );
  }

  updateSupplierManager(
    request: IpSupplierManager,
    id: number
  ): Observable<IpSupplierManager> {
    return this.http.put<IpSupplierManager>(
      `${this.api}/invopay/suppliers/${id}`,
      request
    );
  }

  // // TODO: Remove method
  // deleteSupplierManager(id: number): Observable<Object> {
  //   return this.http.delete(`${this.api}/invopay/suppliers/${id}`);
  // }

  // SUPPLIER USERS

  getSupplierUsers(pageable: Pageable = {}) {
    let params = new HttpParams();
    if (typeof pageable.page !== 'undefined') {
      params = params.set('page', pageable.page);
    }
    if (pageable.size) {
      params = params.set('size', pageable.size);
    }
    if (pageable.sort) {
      params = params.set('sort', pageable.sort);
    }
    return this.http.get<PageResponse<UserResponse>>(
      `${this.api}/invopay/suppliers/user`,
      { params }
    );
  }

  createSupplierUser(request: NewSupplierUser) {
    return this.http.post(`${this.api}/invopay/suppliers/user`, request);
  }

  deleteSupplierUser(id: number) {
    return this.http.delete(`${this.api}/invopay/suppliers/user/${id}`);
  }

  // PURCHASE ORDER / CONTRACT ASSIGNMENTS

  getAssignedProjectsBySupplierId(
    id: number
  ): Observable<Array<AssignedProject>> {
    return this.http.get<Array<AssignedProject>>(
      `${this.api}/invopay/suppliers/purchase-orders-contracts/${id}`
    );
  }

  getAvailableProjects(): Observable<Array<AvailableProject>> {
    return this.http.get<Array<AvailableProject>>(
      `${this.api}/invopay/enterprises/purchase-orders-contracts`
    );
  }

  assignProject(project: AssignProject) {
    const projects = [project];
    // (Because it actually expects an array of projects)
    return this.http.post(
      `${this.api}/invopay/suppliers/assign`,
      projects
    );
  }

  unassignProject(project: AssignProject) {
    const projects = [project];
    // (Because it actually expects an array of projects)
    return this.http.delete(
      `${this.api}/invopay/suppliers/assign`,
      { body: projects }
    );
  }

  // OTHERS

  toFormData(formValue: any) {
    const formData = new FormData();
    for (const key of Object.keys(formValue)) {
      const value = formValue[key];
      if (key === 'address') {
        formData.append('address.country', value.country);
        formData.append('address.zipCode', value.zipCode);
        formData.append('address.streetName', value.streetName);
        formData.append('address.streetNumber', value.streetNumber);
      }
      else if (key === 'account') {
        formData.append('account.accountNumber', value.accountNumber);
        formData.append('account.bank', value.bank);
        formData.append('account.branchNumber', value.branchNumber);
        formData.append('account.ownerName', value.ownerName);
        formData.append('account.currency', value.currency);
        formData.append('account.accountType', value.accountType);
        formData.append('account.extras', value.extras);
      }
      else {
        formData.append(key, value);
      }
    }
    return formData;
  }

  getTaxes(): Observable<Array<TaxDTO>> {
    return this.http.get<Array<TaxDTO>>(
      `${this.api}/invopay/tax/supplier`
    );
  }

  getTaxesBySupplierId(supplierId: number): Observable<Array<TaxDTO>> {
    return this.http.get<Array<TaxDTO>>(
      `${this.api}/invopay/tax/supplier/${supplierId}`
    );
  }

  getSupplierLogoById(supplierId: number): Observable<ArrayBuffer> {
    return this.http.get(
      `${this.api}/invopay/suppliers/logo/${supplierId}`,
      { responseType: 'arraybuffer' }
    );
  }

  getAssignedEnterprises(): Observable<Array<EnterpriseProfile>> {
    return this.http.get<Array<EnterpriseProfile>>(
      `${this.api}/invopay/suppliers/enterprise/assign`
    );
  }

  createAccount(account: AccountRequest): Observable<Account> {
    return this.http.post<Account>(`${this.api}/invopay/suppliers/create-account`, account)
  }

  editAccount(account: AccountRequest): Observable<Account> {
    return this.http.put<Account>(`${this.api}/invopay/suppliers/edit-account`, account)
  }

  getBrokersWithPages(pageable: Pageable = {}, filter: string = ""): Observable<PageResponse<UserResponse>> {
    let params = new HttpParams();
    if (typeof pageable.page !== 'undefined') {
      params = params.set('page', pageable.page);
    }
    if (pageable.size) {
      params = params.set('size', pageable.size);
    }
    if (pageable.sort) {
      params = params.set('sort', pageable.sort);
    }
    if (filter) {
      params = params.set('filter', filter);
    }
    return this.http.get<PageResponse<UserResponse>>(
      `${this.api}/invopay/enterprises/brokers/pages`,
      { params }
    );
  }

  getAllBrokers(): Observable<UserResponse[]> {
    return this.http.get<UserResponse[]>(
      `${this.api}/invopay/enterprises/brokers/all`
    )
  }

  toSelectInputOptions(brokers: UserResponse[]): IpSelectInputOption[] {
    return brokers.map(broker => ({
      value: broker.id.toFixed(),
      label: `${broker.fullName ?? broker.username}`
    }));
  }
}